import type { NumberConfig, NumberField, NumberValue } from '@lighthouse/core'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { scientificNotationToString } from '../../../utils'
import { useAppThemeContext } from '../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../FillPicker'
import { NumberInput } from '../../NumberInput'
import { sizeFontWeighMap, sizeMap } from '../constant'
import { useFieldInputFont } from '../hooks/useFieldInputFont'
import type { FieldBaseProps, Size } from '../types'

interface NumberFieldProps extends FieldBaseProps {
    numberConfig: NumberConfig
    value: NumberValue
    numberField?: NumberField
}

const SCxInput = styled(NumberInput).withConfig<{
    textSize: Size
    customFontSize?: string
    customFontWeight?: number
    placeholderColor?: string
}>({
    shouldForwardProp: p => !['textSize', 'customFontSize', 'customFontWeight', 'placeholderColor'].includes(p)
})`
    width: 100%;

    outline: none;
    font-size: ${({ textSize, customFontSize }) => customFontSize || `${sizeMap[textSize]}px`};
    font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
    line-height: ${({ customFontSize }) => (customFontSize ? 'normal' : undefined)};
    overflow: hidden;
    color: inherit;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    /* appearance: none; */
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    /* Firefox */
    appearance: textfield;

    &[readonly] {
        cursor: not-allowed;
    }

    ::placeholder {
        font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
        color: ${({ placeholderColor }) => placeholderColor || 'var(--color-gray-300)'};
    }
`

const SCxContainer = styled.div<{ customFontSize?: string }>`
    width: 100%;
    padding: 8px;
    display: flex;
    min-height: ${({ customFontSize }) => (customFontSize ? `calc(1.4 * ${customFontSize} + 2px)` : '38px')};
    line-height: 22px;
    align-items: center;
    gap: 6px;
    font-size: var(--font-size-normal);
`

const SCxSign = styled.div`
    white-space: nowrap;
    min-width: 24px;
    text-align: center;
`

const NumberTextField: React.FunctionComponent<NumberFieldProps> = props => {
    const {
        readOnly,
        numberConfig,
        config: { size = 'middle', placeholder, breakPoint, autofocus },
        onBlur,
        onChange,
        value,
        numberField,
        onKeyDown,
        onFocus
    } = props
    const { theme } = useAppThemeContext()
    const { number } = numberField ?? {}
    const mode = 'number'
    const { accuracy = 0, suffix, prefix } = number ?? {}
    // 字符串原样展示，保留输入中间态（如 "0."、"0.0"）；数字用 scientificNotationToString 以支持科学计数法
    const text = useMemo(() => (typeof value === 'string' ? value : scientificNotationToString(value)), [value])
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [isFocus, setIsFocus] = useState(false)
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const { customFontSize, customFontWeight } = useFieldInputFont(breakPoint)
    const showValue = useMemo(() => {
        const isEmptyString = text === ''
        if (isEmptyString) {
            return ''
        }
        const finishNumber = mode === 'number' ? Number(text) : Number(text) * 100
        return finishNumber.toFixed(accuracy)
    }, [accuracy, mode, text])

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const targetValue = ev.target.value
            onChange?.({ type: 'number', value: targetValue })
        },
        [onChange]
    )

    const handleBlur = useCallback(() => {
        setIsFocus(false)
        onBlur?.({ type: 'number', value: scientificNotationToString(text) })
    }, [onBlur, text])

    const handleFocus = useCallback(() => {
        if (readOnly) {
            return
        }
        setIsFocus(true)
        onFocus?.()
    }, [onFocus, readOnly])

    return (
        <SCxContainer data-field-input data-field-active={isFocus} data-field-disabled={!!readOnly} customFontSize={customFontSize}>
            {prefix && !isFocus && <SCxSign data-field-icon>{prefix}</SCxSign>}
            <SCxInput
                ref={inputRef}
                readOnly={readOnly}
                placeholder={placeholder}
                placeholderColor={placeholderColor}
                autoFocus={autofocus}
                value={text}
                type="number"
                // inputMode="numeric"
                textSize={size}
                customFontSize={customFontSize}
                customFontWeight={customFontWeight}
                onFocus={handleFocus}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={onKeyDown}
                style={{ flex: 1 }}
            />
            {suffix && !isFocus && <SCxSign data-field-icon>{suffix}</SCxSign>}
        </SCxContainer>
    )
}

export default NumberTextField
